/**
 * What to tell a retailer about the amount they have typed (30/30/30/10 milestone payments).
 *
 * Pure, and string-only: money is never parsed to a JS number (rule 1). Amounts are compared as
 * integers of 1/10,000, which is the stored precision.
 *
 *  - `over`    more than the API will accept right now → the Submit button is disabled
 *  - `under`   less than the amount due now           → allowed, with a plain warning
 *  - `ok`      due now, or between due now and the most payable
 *  - `invalid` not an amount at all, or zero
 *  - `empty`   nothing typed yet
 */
export type AmountNotice =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'over'; mostPayable: string }
  | { kind: 'under'; due: string; stillOwed: string }
  | { kind: 'ok' };

const PATTERN = /^\d{1,15}(\.\d{1,4})?$/;

/** '12.5' -> 125000n. Throws on anything that is not a plain non-negative decimal. */
function toUnits(value: string): bigint {
  const [whole = '0', frac = ''] = value.trim().split('.');
  return BigInt(whole) * 10_000n + BigInt(frac.padEnd(4, '0').slice(0, 4));
}

/** 125000n -> '12.5000'. */
function fromUnits(units: bigint): string {
  const whole = units / 10_000n;
  const frac = (units % 10_000n).toString().padStart(4, '0');
  return `${whole}.${frac}`;
}

export function noticeFor(
  typed: string,
  figures: { amountDueNow: string; payableNow: string },
): AmountNotice {
  const t = typed.trim();
  if (t === '') return { kind: 'empty' };
  if (!PATTERN.test(t)) return { kind: 'invalid' };
  const amount = toUnits(t);
  if (amount === 0n) return { kind: 'invalid' };

  const most = toUnits(figures.payableNow);
  const due = toUnits(figures.amountDueNow);
  if (amount > most) return { kind: 'over', mostPayable: fromUnits(most) };
  if (amount < due) return { kind: 'under', due: fromUnits(due), stillOwed: fromUnits(due - amount) };
  return { kind: 'ok' };
}
